import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { User, UserResponse } from '@/types';
import {
  createUser,
  createUserAuth0,
  CreateUserAuth0TypesRequest,
  CreateUserTypesRequest,
  getOrgUsers,
  getUserById,
  getUsersByQuery,
  softUserDelete,
  updateUser,
  UserQuery,
} from '@/services/User/user.service';
import { PaginationResponse } from '@/hooks/usePaginationFilter';

export const userApiSlice = createApi({
  reducerPath: 'userApi',
  tagTypes: ['UserApi'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getUsers: builder.query<PaginationResponse<User>, UserQuery>({
      queryFn: async (query) => {
        const data = await getOrgUsers(query);
        return { data };
      },
      providesTags: ['UserApi'],
    }),
    getUserById: builder.query<User, { userId: string; organizationId: string; companyId: string }>(
      {
        queryFn: async ({ userId, organizationId, companyId }) => {
          const data = await getUserById(userId, organizationId, companyId);
          return { data };
        },
        providesTags: ['UserApi'],
      },
    ),
    getUsersByQuery: builder.query<
      User[],
      { organizationId: string; companyId: string; query?: Partial<User> }
    >({
      queryFn: async ({ organizationId, companyId, query }) => {
        const data = await getUsersByQuery(organizationId, companyId, query ?? {});
        return { data };
      },
      providesTags: ['UserApi'],
    }),
    createUser: builder.mutation<UserResponse, CreateUserTypesRequest>({
      queryFn: async ({ organizationId, body, companyId }) => {
        const user = await createUser({ organizationId, body, companyId });
        return { data: user as UserResponse };
      },
      invalidatesTags: ['UserApi'],
    }),
    createUserAuth0: builder.mutation<UserResponse, CreateUserAuth0TypesRequest>({
      queryFn: async (payload) => {
        const user = await createUserAuth0(payload);
        return { data: user as UserResponse };
      },
      invalidatesTags: ['UserApi'],
    }),
    updateUser: builder.mutation<
      User | undefined,
      {
        userId: string;
        body: Partial<User>;
        organizationId: string;
        companyId: string;
      }
    >({
      queryFn: async ({ userId, body, organizationId, companyId }) => {
        const user = await updateUser(userId, body, organizationId, companyId);
        return { data: user };
      },
      invalidatesTags: ['UserApi'],
    }),
    deleteUser: builder.mutation<
      any,
      { userId: string; organizationId: string; companyId: string }
    >({
      queryFn: async ({ userId, organizationId, companyId }) => {
        const result = await softUserDelete(userId, organizationId, companyId);
        return { data: result };
      },
      invalidatesTags: ['UserApi'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUsersByQueryQuery,
  useCreateUserAuth0Mutation,
} = userApiSlice;
